<svelte:options
	customElement={{
		tag: 'rf-mirror',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	/**
	 * Types
	 */
	export interface ReframeMirrorProps {
		id?: string;
		tag: string;
		debug?: boolean;
		reverse?: boolean;
		direction?: 'horizontal' | 'vertical';
		fit?: 'width' | 'height' | 'optimized';
		responsive?: boolean;
		'no-collection'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import classNames from 'classnames';
	import { v4 as uuidv4 } from 'uuid';
	import RfNotificationCentre from '@core/elements/rf-notification-centre/rf-notification-centre.svelte';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import { getComponentNotifications } from '@store/notification-centre.svelte';
	import parseBool from '@src/utils/bool';
	import { createEventDispatcher, onMount, onDestroy } from 'svelte';
	import { get } from 'svelte/store';
	import cssVariables from '@src/utils/cssvariables';
	import type { RFCanvas } from '@src/types/reframe';

	/**
	 * Props
	 */
	const {
		id = undefined,
		tag = 'section',
		direction = 'horizontal',
		fit = 'width',
		debug = false,
		reverse = false,
		responsive = true,
		'no-collection': noCollection = false,
	}: ReframeMirrorProps = $props();

	/**
	 * Constants
	 */
	export const name = 'rf-mirror';
	const componentId = uuidv4();

	/**
	 * Variables
	 */
	let containerHeight: string = $state('0');
	let observer: ResizeObserver | null = null;
	let itemObservers: ResizeObserver[] = [];

	/**
	 * Derived
	 */
	const collectionNotifs = $derived(getComponentNotifications(componentId));

	/**
	 * Composables
	 */

	const { element, forEachSlottedElement, getSlottedElements } = useSlottedElements();

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Methods
	 */
	const dispatch = createEventDispatcher();

	const resize = () => {
		dispatch('resize');
		onItemResize();
	};

	const onItemResize = () => {
		const items = getSlottedElements();
		const canvases: RFCanvas[] = items.filter((el) => {
			return el.tagName === 'RF-CANVAS';
		}) as RFCanvas[];

		if (fit === 'height' && canvases.length) heightFitLayout(canvases);
		if (fit === 'optimized' && canvases.length) optimizedFitLayout(canvases);
	};

	/**
	 * Calculates and sets the container height so that all canvases fit side by side,
	 * respecting their aspect ratios and the CSS gap between them.
	 *
	 * How it works:
	 * - Retrieves the container element.
	 * - Sums up the width-to-height ratios of all canvases.
	 * - Gets the container's width and CSS gap value.
	 * - Calculates the height by dividing the available width (minus total gaps) by the total sum of ratios.
	 * - Assigns the result (in pixels) to `containerHeight`.
	 *
	 * @param canvases Array of canvases with naturalWidth and naturalHeight properties.
	 */
	const heightFitLayout = (canvases: RFCanvas[]) => {
		const container = get(element);
		if (!container) return;
		let totalRatios = 0;

		canvases.forEach((item) => {
			const ratio = item.naturalWidth / item.naturalHeight;

			item.style.setProperty('--aspect-ratio', !isNaN(ratio) ? ratio.toString() : '0');

			totalRatios += !isNaN(ratio) ? ratio : 0;
		});

		const bounds = container.getBoundingClientRect();
		const styles = getComputedStyle(container);
		const gap = styles.getPropertyValue('gap').trim();

		containerHeight = `${(bounds.width - parseFloat(gap) * (canvases.length - 1)) / totalRatios}px`;
	};

	/**
	 * Calculates and sets widths for each canvas so that:
	 * - All canvases fit into the container's width.
	 * - Each canvas takes up an equal *visual surface area* (width * height), despite differing aspect ratios.
	 *
	 * How it works:
	 * - Retrieves the container element and its width.
	 * - Computes the aspect ratios of each canvas (height/width).
	 * - Uses an equal-surface formula:
	 *   The width of each canvas is proportional to the square root of (1 / aspect ratio),
	 *   ensuring each canvas occupies the same surface area.
	 * - Scales all provisional widths to perfectly fit the container width.
	 * - Sets the computed widths via a CSS variable (`--rf-mirror-canvas-width`) for each canvas.
	 *
	 * @param canvases Array of canvases with naturalWidth and naturalHeight properties.
	 */
	const optimizedFitLayout = (canvases: RFCanvas[]) => {
		const el = get(element);
		if (!el || canvases.length === 0) return;

		const containerWidth = el.clientWidth;

		// Calculate aspect ratios (A[i] = height/width)
		const aspectRatios = canvases.map((c) => c.naturalHeight / c.naturalWidth);

		// Start with provisional widths assuming equal surface areas
		const tempWidths = aspectRatios.map((A) => Math.sqrt(1 / A));

		// Sum provisional widths
		const tempSum = tempWidths.reduce((acc, w) => acc + w, 0);

		// Scale to fit exactly into the container width
		const scale = containerWidth / tempSum;

		// Set calculated widths on each canvas via CSS custom property
		canvases.forEach((canvas, i) => {
			const w = tempWidths[i] * scale;
			canvas.style.setProperty('--rf-mirror-canvas-width', `${w}px`);
		});
	};

	/**
	 * Hook
	 */
	onMount(() => {
		const el = get(element);

		if (!el) return;

		observer = new ResizeObserver(resize);
		observer.observe(el);

		forEachSlottedElement((item) => {
			const resizeObserver = new ResizeObserver(onItemResize);
			resizeObserver.observe(item);
			itemObservers.push(resizeObserver);
			item.addEventListener('load', onItemResize);
		});
	});

	onDestroy(() => {
		if (observer) observer.disconnect();

		itemObservers.forEach((observer) => {
			observer.disconnect();
		});
	});
</script>

<slot name="header"></slot>
<svelte:element
	this={tag}
	class={classNames('rf-mirror', `${direction}`, `fit-${fit}`, {
		'rf-mirror--reverse': parseBool(reverse),
		responsive: parseBool(responsive),
	})}
	bind:this={$element}
	part="rf-mirror"
	style={cssVariables({ '--rf-mirror-height': containerHeight })}
>
	{#if collectionNotifs && debug}
		<RfNotificationCentre notifications={collectionNotifs} />
	{/if}
	<slot></slot>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-mirror {
		container-type: inline-size;
		container-name: rf-mirror;
		font-family: var(--rf-font-family);
		display: flex;
		gap: var(--rf-col-gap);
		&.horizontal {
			flex-flow: row nowrap;

			::slotted(*) {
				width: calc(100% * var(--rf-mirror-canvas-col, 6) / var(--rf-grid, 12));
				padding-left: calc(100% * var(--rf-mirror-canvas-offset, 0) / var(--rf-grid, 12) - var(--rf-col-gap));
			}
			&.responsive {
				@container rf-mirror (width < #{breakpoint.$xs}) {
					flex-flow: column nowrap;

					::slotted(*) {
						width: 100%;
					}
				}
			}
		}

		&.vertical {
			flex-flow: column nowrap;
			width: calc(100% * var(--rf-mirror-canvas-col, 12) / var(--rf-grid, 12));
			padding-left: calc(100% * var(--rf-mirror-canvas-offset, 0) / var(--rf-grid, 12) - var(--rf-col-gap));

			// first content
			::slotted(*) {
				width: 100%;
			}
		}

		&--reverse.vertical {
			flex-direction: column-reverse;
		}

		&--reverse.horizontal {
			flex-direction: row-reverse;
		}

		&.fit-height {
			width: 100%;
			&.horizontal {
				height: var(--rf-mirror-height, auto);

				::slotted(*) {
					height: fit-content;
					width: calc(100% * var(--rf-mirror-canvas-col, 6) / var(--rf-grid, 12));
					padding-left: calc(100% * var(--rf-mirror-canvas-offset, 0) / var(--rf-grid, 12) - var(--rf-col-gap));
				}
				::slotted(rf-canvas) {
					height: 100%;
					--rf-canvas-height: 100%;
					--rf-canvas-panel-height: 100%;
					--atlas-container-height: 100%;
					width: auto;
					aspect-ratio: var(--aspect-ratio);
				}
			}

			&.vertical {
				::slotted(*) {
					height: 50vh;

					--rf-canvas-height: 100%;
					--rf-canvas-panel-height: 100%;
					--atlas-container-height: 100%;
				}
			}
		}

		&.fit-optimized.horizontal {
			width: 100%;

			::slotted(*) {
				height: fit-content;
				width: calc(100% * var(--rf-mirror-canvas-col, 6) / var(--rf-grid, 12));
				padding-left: calc(100% * var(--rf-mirror-canvas-offset, 0) / var(--rf-grid, 12) - var(--rf-col-gap));
			}
			::slotted(rf-canvas) {
				width: var(--rf-mirror-canvas-width);
				height: fit-content;
			}
			&.responsive {
				@container rf-mirror (width < #{breakpoint.$xs}) {
					::slotted(*) {
						width: 100%;
					}
				}
			}
		}
		::slotted(rf-canvas) {
			overflow: hidden;
		}
	}
</style>
